import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  PasswordInput,
  ScrollArea,
  TextInput,
  Stack,
  Checkbox,
  FileInput,
  SimpleGrid,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_SALES } from "apollo/mutuations";
import { GET_REGIONS, GET_SALESES } from "apollo/queries";
import ImageUploader from "components/controls/ImageUpload";
import { customLoader } from "components/utilities/loader";
import { useEffect, useRef, useState } from "react";

export const SalesAddModal = ({ setOpened, size, activePage }) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [regionDropDownData, setRegionDropDownData] = useState([]);

  const form = useForm({
    initialValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      address: "",
      gender: "",
      region: "1",
      city: "",
      subcity: "",
      woreda: "",
    },
    validate: {
      phone: (value) =>
        value.length > 13 || value.length < 10
          ? "Phone number must be exactly 13 characters"
          : null,
    },
  });

  const { loading: region_loading, refetch } = useQuery(GET_REGIONS, {
    variables: { first: 100, page: 1 },
    onCompleted(data) {
      const newArr = data.regions.data.map((element) => ({
        label: element.name,
        value: element.id,
      }));
      setRegionDropDownData(newArr);
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get region",
      });
    },
  });
  useEffect(() => {
    refetch();
  }, []);
  const { height } = useViewportSize();
  const [addSales, { loading: salesLoading }] = useMutation(CREATE_SALES, {
    update(cache, { data: { createSales } }) {
      cache.updateQuery(
        {
          query: GET_SALESES,
          variables: {
            first: parseInt(10),
            page: 1,
            search: "",
            ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
          },
        },
        (data) => {
          if (data) {
            return {
              getSales: {
                ...data.getSales,
                data: [createSales, ...data.getSales.data],
              },
            };
          }
        },
      );
    },
  });

  const [isMaleChecked, setIsMaleChecked] = useState(false);
  const [isFemaleChecked, setIsFemaleChecked] = useState(false);

  const handleMaleChange = (event) => {
    setIsMaleChecked(event.target.checked);
    form.setFieldValue("gender", "Male");
    setIsFemaleChecked(false);
  };

  const handleFemaleChange = (event) => {
    form.setFieldValue("gender", "Female");
    setIsFemaleChecked(event.target.checked);
    setIsMaleChecked(false);
  };
  const handleFileChange = (event) => {
    setFiles(files);
  };
  const submit = () => {
    addSales({
      variables: {
        name: form.getInputProps("name").value,
        email: form.getInputProps("email").value,
        password: form.getInputProps("password").value,
        phone: form.getInputProps("phone").value,
        city: form.getInputProps("city").value,
        woreda: form.getInputProps("woreda").value,
        subcity: form.getInputProps("subcity").value,
        gender: form.getInputProps("gender").value,
        region_id: form.getInputProps("region").value,
        address: form.getInputProps("address").value,
        profile_image: files[files.length - 1],
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Sales Created Successfully",
        });

        setOpened(false);
      },
      onError(error) {
        console.log(error);
        setOpened(true);

        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Create Sales",
        });
      },
    });
  };
  const setRegionDropDownValue = (val) => {
    form.setFieldValue("region", val);
  };

  return (
    <>
      <LoadingOverlay
        visible={salesLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  required
                  label="Name"
                  placeholder="Name"
                  {...form.getInputProps("name")}
                />
                <TextInput
                  required
                  label="Email"
                  placeholder="Email"
                  {...form.getInputProps("email")}
                />
                <TextInput
                  required
                  label="Phone"
                  placeholder="Phone"
                  {...form.getInputProps("phone")}
                  maxLength={13}
                  {...form.getInputProps("phone")}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/[^0-9+]/g, "");
                  }}
                />
                <PasswordInput
                  placeholder="Password"
                  label="Password"
                  {...form.getInputProps("password")}
                />
                <TextInput
                  required
                  label="Woreda"
                  placeholder="Woreda"
                  {...form.getInputProps("woreda")}
                />
                <div style={{ marginTop: "10px" }}>
                  <ImageUploader
                    accept="image/png, image/jpeg, image/jpg"
                    buttonLabel="Profile Image"
                    onChange={handleFileChange}
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={4}>
                <TextInput
                  required
                  label="Address"
                  placeholder="Address"
                  {...form.getInputProps("address")}
                />
                <Select
                  label="Select Region"
                  searchable
                  data={regionDropDownData}
                  value={form.getInputProps("region").value.toString()}
                  onChange={setRegionDropDownValue}
                />
                <TextInput
                  required
                  label="City"
                  placeholder="City"
                  {...form.getInputProps("city")}
                />
                <TextInput
                  placeholder="SubCity"
                  label="Sub City"
                  {...form.getInputProps("subcity")}
                />
                <div style={{ display: "flex", marginTop: "25px" }}>
                  <Checkbox
                    checked={isMaleChecked}
                    onChange={handleMaleChange}
                    label="Male"
                  />
                  <Checkbox
                    style={{ marginLeft: "10px" }}
                    checked={isFemaleChecked}
                    onChange={handleFemaleChange}
                    label="Female"
                  />
                </div>
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Button
                  style={{
                    width: "25%",
                    marginTop: "15px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  type="submit"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};
